import React, { useRef, useEffect } from 'react';
import { ViewportState } from '../../store/viewportStore';
import { MinimapTransform } from './MinimapUtils';
import { FloorBounds } from '../../hooks/useFloorBounds';

interface ViewportRectProps {
  viewport: ViewportState;
  bounds: FloorBounds;
  transform: MinimapTransform;
  onPan: (newPanX: number, newPanY: number) => void;
}

export const ViewportRect: React.FC<ViewportRectProps> = ({
  viewport,
  bounds,
  transform,
  onPan,
}) => {
  const isDragging = useRef(false);
  const dragStart = useRef({ mouseX: 0, mouseY: 0, panX: 0, panY: 0 });
  const pendingPanRef = useRef<{ panX: number; panY: number } | null>(null);
  const panRafRef = useRef<number | null>(null);
  const onPanRef = useRef(onPan);
  onPanRef.current = onPan;

  // Calculate viewport box in world space
  const worldLeft = -viewport.panX / viewport.zoom;
  const worldTop = -viewport.panY / viewport.zoom;
  const worldWidth = viewport.canvasWidth / viewport.zoom;
  const worldHeight = viewport.canvasHeight / viewport.zoom;

  // Convert to minimap space
  const miniLeft = (worldLeft - bounds.minX) * transform.scale + transform.offsetX;
  const miniTop = (worldTop - bounds.minY) * transform.scale + transform.offsetY;
  const miniWidth = worldWidth * transform.scale;
  const miniHeight = worldHeight * transform.scale;

  // Constrain bounds of the viewport box so it remains visible or at least interacts nicely
  const minDimension = 12; // Enforce minimum size in pixels for ease of dragging
  const width = Math.max(minDimension, miniWidth);
  const height = Math.max(minDimension, miniHeight);

  // If the box is extremely small, offset it so it centers on the actual viewport center
  const displayLeft = miniLeft - (width - miniWidth) / 2;
  const displayTop = miniTop - (height - miniHeight) / 2;

  const schedulePan = (panX: number, panY: number) => {
    pendingPanRef.current = { panX, panY };
    if (panRafRef.current != null) return;
    panRafRef.current = requestAnimationFrame(() => {
      panRafRef.current = null;
      const pending = pendingPanRef.current;
      if (!pending) return;
      onPanRef.current(pending.panX, pending.panY);
    });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    isDragging.current = true;
    dragStart.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      panX: viewport.panX,
      panY: viewport.panY,
    };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      e.preventDefault();

      const dx = e.clientX - dragStart.current.mouseX;
      const dy = e.clientY - dragStart.current.mouseY;

      const worldDx = dx / transform.scale;
      const worldDy = dy / transform.scale;

      const nextPanX = dragStart.current.panX - worldDx * viewport.zoom;
      const nextPanY = dragStart.current.panY - worldDy * viewport.zoom;

      schedulePan(nextPanX, nextPanY);
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      pendingPanRef.current = null;
      if (panRafRef.current != null) {
        cancelAnimationFrame(panRafRef.current);
        panRafRef.current = null;
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (panRafRef.current != null) cancelAnimationFrame(panRafRef.current);
    };
  }, [transform.scale, viewport.zoom]);

  return (
    <div
      className="absolute border-2 border-blue-500 bg-blue-500/15 cursor-move rounded shadow-[0_0_8px_rgba(59,130,246,0.5)] transition-[border-color,background-color] hover:bg-blue-500/25 active:bg-blue-500/35"
      style={{
        left: displayLeft,
        top: displayTop,
        width,
        height,
      }}
      onMouseDown={handleMouseDown}
    />
  );
};
